import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js'
import i18n from '../../translations/i18n'
function validateIban(value: string): string | null {
    if (!value) {
        return i18n.t('contractCreator.validation.fieldRequired');
    }

    // General high-level regex pattern for IBAN: 
    // Country Code(2 letters), Check Digits(2 numbers), Basic Bank Account Number(up to 30 alphanumeric characters)
    const ibanRegex = /^([a-zA-Z]{2})(\d{2})([a-zA-Z0-9]{1,30})$/;

    if (!ibanRegex.test(value)) {
        return i18n.t('contractCreator.validation.invalidIBAN');
    }
    return null;
}

function validateEmail(value: string): string | null {
    if (!value) {
        return i18n.t('contractCreator.validation.fieldRequired');
    }

    // Regex pattern for a general email:
    // Local part(at least one character before @), @, domain name(at least one character after @)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailRegex.test(value)) {
        return i18n.t('contractCreator.validation.invalidEmail');
    }
    return null;
}

function validateDateField(value: string): string | null {
    // Check if the value is empty
    if (!value) {
        return i18n.t('contractCreator.validation.fieldRequired');
    }

    // Check if the value is a valid date
    if (isNaN(Date.parse(value))) {
        return i18n.t('contractCreator.validation.mustBeValidDate');
    }

    return null;
}

const validateYear = (value?: string | number) => {
    // Convert value to string and check if it is empty
    let stringValue = String(value).trim();
    if (!stringValue) {
        return i18n.t('contractCreator.validation.fieldRequired');
    }

    // Check if the value is a 4-digit number
    return !/^\d{4}$/.test(stringValue) ? i18n.t('contractCreator.validation.invalidYear') : undefined;
}

const validatePhoneNumber = (value?: string) => {
    if (!value) return i18n.t('contractCreator.validation.phoneNumberError');
    // Check for spaces in the input
    if (/\s/.test(value)) {
        return i18n.t('contractCreator.validation.phoneNumberError'); // Replace with your actual error message key for spaces in input
    }
    // Prefix with '+358' if it doesn't start with '+'
    const tempValue = value.charAt(0) === '+' ? value : `+358${value}`;
    // Use AsYouType to format the phone number
    const asYouType = new AsYouType();
    asYouType.input(tempValue);
    // Validate the phone number
    return !isValidPhoneNumber(tempValue)
        ? i18n.t('contractCreator.validation.phoneNumberError')
        : undefined;
}

function validateNumberField(value: any, { required = true, minLength = 0, maxLength = Infinity } = {}) {
    if (required && (!value || Number.isNaN(Number(value)))) {
        return i18n.t('contractCreator.validation.fieldRequiredShouldBeNumber');
    }

    if (value.toString().length < minLength) {
        return i18n.t('contractCreator.validation.minLengthError', { minLength: minLength });
    }

    if (value.toString().length > maxLength) {
        return i18n.t('contractCreator.validation.maxLengthError', { maxLength: maxLength });
    }

    return null;
}

function validateField(value: any, { required = true, minLength = 0, maxLength = Infinity } = {}) {
    if (required && (!value || value.trim().length === 0)) {
        return i18n.t('contractCreator.validation.fieldRequired');
    }

    if (value.toString().length < minLength) {
        return i18n.t('contractCreator.validation.minLengthError', { minLength: minLength });
    }

    if (value.toString().length > maxLength) {
        return i18n.t('contractCreator.validation.maxLengthError', { maxLength: maxLength });
    }

    return null;
}

function validatePostalCode(value: string) {
    if (!value || value.trim().length === 0) {
        return i18n.t('contractCreator.validation.fieldRequired');
    }
    return value && !/^[0-9]{1,5}$/i.test(value)
        ? i18n.t('validation.postalCode')
        : undefined
}

export const generateValidationSchema = (contract: any) => {
    let tenantValidations = {};
    for (let i = 0; i < contract.tenants.length; i++) {
        tenantValidations[`tenantPhoneNumber${i}`] = validatePhoneNumber;
        tenantValidations[`tenantEmail${i}`] = validateEmail;
        tenantValidations[`tenantName${i}`] = (value: any) => validateField(value, { minLength: 1, maxLength: 100 })
        tenantValidations[`tenantSSN${i}`] = (value: any) => validateField(value, { minLength: 1, maxLength: 50 })
        tenantValidations[`tenantAddress${i}`] = (value: any) => validateField(value, { minLength: 1, maxLength: 50 })
    }

    let landlordValidations = {};
    if (contract.additionalLandlords && contract.additionalLandlords.length > 0) {
        for (let i = 0; i < contract.additionalLandlords.length; i++) {
            landlordValidations[`additionalLandlordName${i}`] = (value: any) => validateField(value, { minLength: 1, maxLength: 50 });
            landlordValidations[`additionalLandlordEmail${i}`] = validateEmail;
        }
    }

    return {
        name: (value: any) => validateField(value, { minLength: 1, maxLength: 50 }),
        iban: validateIban,
        email: validateEmail,
        landlordAddress: (value: any) => validateField(value, { minLength: 1, maxLength: 50 }),
        apartmentAddress: (value: any) => validateField(value, { minLength: 1, maxLength: 50 }),
        phoneNumber: validatePhoneNumber,
        rentPaymentDay: (value: any) => validateField(value, { minLength: 1, maxLength: 50 }),
        numberOfKeys: (value: any) => validateField(value, { minLength: 1, maxLength: 80 }),
        firstDismissalDate: validateDateField,
        availableUntilDate: validateDateField,
        buildYear: validateYear,
        buildingType: (value: any) => validateField(value, { minLength: 1, maxLength: 40 }),
        condition: (value: any) => validateField(value, { minLength: 1, maxLength: 50 }),
        layout: (value: any) => validateField(value, { minLength: 1, maxLength: 50 }),
        squareMeters: (value: any) => validateNumberField(value, { minLength: 1, maxLength: 6 }),
        rentAmount: (value: any) => validateField(value, { minLength: 1, maxLength: 50 }),
        startDate: validateDateField,
        landlordStreet: (value: any) => validateField(value, { minLength: 1, maxLength: 100 }),
        landlordPostalCode: validatePostalCode,
        landlordCity: (value: any) => validateField(value, { minLength: 1, maxLength: 50 }),
        ...tenantValidations,
        ...landlordValidations,
    };
}

export function validateForm(formData: any, validationSchema: any): any {
    const errors = {};
    for (const [field, value] of Object.entries(formData)) {
        if (validationSchema[field]) {
            const errorMessage = validationSchema[field](value);
            if (errorMessage) {
                errors[field] = errorMessage;
            }
        }
    }
    return errors;
}
